/**
 * TonnageChart — total weight moved (sets × reps × weight) per session,
 * plotted over time as a line chart.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import LineChart from './LineChart';

export default function TonnageChart() {
  const data = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 90 * 86_400_000).toISOString();
    const workouts = await db.workouts
      .where('startedAt').aboveOrEqual(cutoff)
      .filter((w) => w.status === 'completed')
      .toArray();

    const results = await Promise.all(
      workouts.map(async (w) => {
        const sets = await db.loggedSets
          .where('workoutId').equals(w.id)
          .filter((s) => !s.isWarmup)
          .toArray();
        const tonnage = sets.reduce((sum, s) => sum + s.weightLbs * s.reps, 0);
        return { date: w.startedAt.slice(0, 10), value: Math.round(tonnage) };
      }),
    );

    // Collapse same-day sessions
    const byDate = new Map<string, number>();
    for (const r of results) {
      byDate.set(r.date, (byDate.get(r.date) ?? 0) + r.value);
    }

    return Array.from(byDate.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, []) ?? [];

  if (data.length < 2) return null;

  return (
    <section>
      <p className="section-header">Session Tonnage (90 Days)</p>
      <div className="card">
        <LineChart data={data} unit="lbs moved" color="#f59e0b" height={120} />
        <div className="flex justify-between mt-3 px-1">
          <div>
            <p className="text-xs text-ink-faint">Best session</p>
            <p className="text-lg font-bold text-warning font-mono">
              {Math.max(...data.map((d) => d.value)).toLocaleString()} lbs
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-ink-faint">Avg/session</p>
            <p className="text-lg font-bold text-ink font-mono">
              {Math.round(data.reduce((s, d) => s + d.value, 0) / data.length).toLocaleString()} lbs
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
